import React, { useState, useMemo, useEffect } from "react";
import { FloatButton, Badge, Button } from "antd";
import { MessageOutlined, CloseOutlined } from "@ant-design/icons";
import ChatList from "./ChatList";
import ChatWindow from "./ChatWindow";
import { mockChats as initialChats } from "./mockData";
import "./Chat.css";
import api from "../../services/api";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/AuthContext";
import { useSocket } from "../../hooks/SocketContext";

// Som de notificação embutido em Base64 para garantir a reprodução
const notificationSoundData =
  "data:audio/mpeg;base64,/+MYxAAEaAIEeUAQAgBgNgP/////KQQ/////Lvrg+lcWYHgtjadzsbTq+yREu495tq9c6v/7vt/of7mna9v6/btUnU17Jun9/+MYxCkT26KW+YGBAj9v6vUh+zab//v/96C3/pu6H+pv//r/ycIIP4pcWWTRBBBAMXgNdbRaABQAAABRWKwgjQVX0ECmrb///+MYxBQSM0sWWYI4A++Z/////////////0rOZ3MP//7H44QEgxgdvRVMXHZseL//540B4JAvMPEgaA4/0nHjxLhRgAoAYAgA/+MYxAYIAAJfGYEQAMAJAIAQMAwX936/q/tWtv/2f/+v//6v/+7qTEFNRTMuOTkuNVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";

const Chat = () => {
  const { user } = useAuth();
  const { socket } = useSocket();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showClosed, setShowClosed] = useState(false);
  const [countMessages, setCountMessages] = useState(0);
  const [chats, setChats] = useState([]);

  useEffect(() => {
    if (!selectedChat) {
      loadChats();
    }
  }, [selectedChat]);

  useEffect(() => {
    let countMessages = 0;
    chats.map((item) => {
      countMessages += item.messages.filter((data) => {
        return data.send_id != user.id && !data.is_viewed;
      }).length;
    });
    setCountMessages(countMessages);
  }, [chats]);

  useEffect(() => {
    if (!socket.connected) {
      socket.connect();
    }

    socket.on("connect", () => {});

    socket.on("newChat", (ticket) => {
      if (ticket.user_id || ticket.collaborator_id) {
        setChats((prevTickets) => [ticket, ...prevTickets]);
      }
    });

    socket.on("acceptChat", (ticket) => {
      setChats((prevChats) =>
        prevChats.map((item) => {
          if (item.id == ticket.id) {
            return ticket;
          }
          return item;
        })
      );
    });

    socket.on("createChat", (ticket) => {
      playNotificationSound();
      setChats((prevChats) => [ticket, ...prevChats]);
    });

    socket.on("closedChat", (ticket) => {
      setChats((prevTickets) =>
        prevTickets.filter((item) => item.id != ticket.id)
      );
    });

    socket.on("newMessage", (message) => {
      playNotificationSound();
      setChats((prevTickets) => {
        const targetIndex = prevTickets.findIndex(
          (chat) => chat.id === message.ticket_id
        );

        if (targetIndex === -1) {
          console.warn(`Chat com ID ${message.ticket_id} não encontrado.`);
          return prevTickets;
        }

        const chatToUpdate = prevTickets[targetIndex];

        const remainingChats = prevTickets.filter(
          (chat) => chat.id !== message.ticket_id
        );

        const updatedChat = {
          ...chatToUpdate,
          messages: [message, ...chatToUpdate.messages],
          updated_at: new Date(),
        };
        return [updatedChat, ...remainingChats];
      });
    });

    return () => {
      socket.off("connect");
      socket.off("newChat");
      socket.off("closedChat");
      socket.off("newMessage");
      socket.off("acceptChat");
      socket.off("error");
      socket.disconnect();
    };
  }, [socket, selectedChat]);

  const notificationSound = useMemo(() => {
    if (typeof Audio !== "undefined") {
      const audio = new Audio("/notification.mp3");
      audio.volume = 0.5;
      return audio;
    }
    return null;
  }, []);

  const playNotificationSound = () => {
    if (notificationSound) {
      // Reinicia a reprodução. Necessário para tocar rapidamente em sequência.
      notificationSound.pause();
      notificationSound.currentTime = 0;

      // Tenta tocar e captura o erro de autoplay
      notificationSound.play().catch((error) => {
        // Notifica no console se o navegador bloqueou o autoplay
        console.warn(
          "Reprodução de áudio bloqueada pelo navegador (Autoplay).",
          error
        );
        // Você pode adicionar um tratamento visual aqui, como um ícone vibrando.
      });
    }
  };

  async function loadChats() {
    setIsLoading(true);
    await api
      .get(`/tickets/open`)
      .then((response) => {
        setChats(response.data);
      })
      .catch(({ response }) => {
        setChats([]);
        if (response?.data?.message) {
          toast.warn(response.data.message);
        } else {
          toast.warn(
            "Ocorreu um erro, verifique sua conexão e tente novamente"
          );
        }
      });
    setIsLoading(false);
  }

  return (
    <>
      <FloatButton
        className="chat-float-button-xl"
        icon={isOpen ? <CloseOutlined /> : <MessageOutlined />}
        type="primary"
        onClick={() => {
          const nextIsOpen = !isOpen;
          setIsOpen(nextIsOpen);
          if (!nextIsOpen) {
            setSelectedChat(null);
          }
        }}
        shape="circle"
        badge={{ count: countMessages }}
      />

      {isOpen && (
        <div className="chat-container">
          {selectedChat ? (
            <ChatWindow
              chat={selectedChat}
              onBack={() => {
                setSelectedChat(null);
              }}
            />
          ) : (
            <ChatList
              chats={chats}
              onSelectChat={(chat, countMessagesChat) => {
                setSelectedChat(chat);
                setCountMessages(countMessages - countMessagesChat);
              }}
              onToggleShowClosed={() => {
                setShowClosed(!showClosed);
              }}
              showClosed={showClosed}
            />
          )}
        </div>
      )}
    </>
  );
};

export default Chat;
